import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'

// @coreui/react-pro 4.15.2 advertises a `useColorModes` hook in its type
// definitions but does not ship it in the runtime bundle, so the colour mode is
// managed here instead.
//
// @coreui/coreui-pro 4.6.4 is on the legacy class-based dark theme: every dark
// rule is scoped under `.dark-theme` rather than the `[data-coreui-theme]`
// attribute used from v5. The class therefore goes on <html>.

const STORAGE_KEY = 'aashwasan-color-mode'
const DARK_CLASS = 'dark-theme'

export const COLOR_MODES = ['light', 'dark', 'auto']

const ColorModeContext = createContext({
  colorMode: 'light',
  resolvedMode: 'light',
  setColorMode: () => {},
})

export const useColorMode = () => useContext(ColorModeContext)

const prefersDark = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-color-scheme: dark)').matches

const readStoredMode = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return COLOR_MODES.includes(stored) ? stored : 'light'
  } catch {
    // Private mode / blocked storage: fall back to the light default.
    return 'light'
  }
}

export const ColorModeProvider = ({ children }) => {
  const [colorMode, setColorModeState] = useState(readStoredMode)
  const [systemDark, setSystemDark] = useState(prefersDark)

  // Only relevant while on 'auto', but the listener is cheap and keeping it
  // unconditional avoids re-subscribing every time the mode changes.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return undefined
    }

    const query = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = (event) => setSystemDark(event.matches)

    // Safari < 14 only supports the deprecated addListener signature.
    if (query.addEventListener) {
      query.addEventListener('change', onChange)
      return () => query.removeEventListener('change', onChange)
    }

    query.addListener(onChange)
    return () => query.removeListener(onChange)
  }, [])

  const resolvedMode = colorMode === 'auto' ? (systemDark ? 'dark' : 'light') : colorMode

  useEffect(() => {
    document.documentElement.classList.toggle(DARK_CLASS, resolvedMode === 'dark')
    // Keeps native controls (scrollbars, date pickers, form fields) in step.
    document.documentElement.style.colorScheme = resolvedMode
  }, [resolvedMode])

  const setColorMode = useCallback((mode) => {
    if (!COLOR_MODES.includes(mode)) {
      return
    }

    setColorModeState(mode)

    try {
      localStorage.setItem(STORAGE_KEY, mode)
    } catch {
      // Preference simply won't survive a reload; not worth failing the toggle.
    }
  }, [])

  const value = useMemo(
    () => ({ colorMode, resolvedMode, setColorMode }),
    [colorMode, resolvedMode, setColorMode],
  )

  return <ColorModeContext.Provider value={value}>{children}</ColorModeContext.Provider>
}

ColorModeProvider.propTypes = {
  children: PropTypes.node,
}

export default ColorModeContext
